import { useState } from "react"

import { ExecutionRunRef } from "@vertesia/common"
import { MessageSquareShare, MoreHorizontal, Pen, Share2, Lock, Globe } from "lucide-react"
import { toast } from "sonner"

import { SearchableExecutionRunRef } from "@models/executions"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/tooltip"

interface AgentRunnerHistoryButtonProps {
    searchableRun: SearchableExecutionRunRef
    updateRun: (id: string, run: SearchableExecutionRunRef) => void
}
export function AgentRunnerHistoryButton({ searchableRun, updateRun }: Readonly<AgentRunnerHistoryButtonProps>) {
    const { client, project } = useUserSession()

    const [open, setOpen] = useState(false)
    const [rename, setRename] = useState(false)

    const run: ExecutionRunRef = searchableRun.run

    const runId = run?.tags?.find((tag) => tag.startsWith("wf-run"))?.split(":")[1]
    const workflowId = run?.tags?.find((tag) => tag.startsWith("wf:generation"))?.split("wf:")[1]
    const collectionId = searchableRun.collection_id
    const [name, setName] = useState(searchableRun.name)

    const isCurrent = checkIfCurrent(runId, workflowId, searchableRun)

    const handleOnOpen = () => {
        if (rename) {
            return
        }

        const hashId = extractHashId(searchableRun)
        if (!hashId) {
            return
        }
        window.location.hash = hashId
        window.location.reload()
    }

    const handleOnShare = () => {
        const hashId = extractHashId(searchableRun)
        if (!hashId || !project) {
            return
        }

        const url = `${new URL(window.location.origin).toString()}assistant?a=${project.account}&p=${project.id}${hashId}`
        navigator.clipboard.writeText(url)
        toast.success("Link copied to clipboard", {
            description: `You can share this link with others to view the ${searchableRun.run_id ? "agent" : "chat"} history.`,
            duration: 2000,
        })

        if (open) {
            setOpen(false)
        }
    }

    const handleRenameReset = () => {
        setName(searchableRun.name)
        setRename(false)
    }

    const handleUpdateCollection = async () => {
        if (!collectionId || !name) {
            return
        }

        const collection = await client.store.collections.retrieve(collectionId)
        if (!collection) {
            return
        }

        const payload = {
            name: name,
            properties: collection.properties,
        }

        client.store.collections.update(collectionId, payload).then(() => {
            toast.success("Agent Task Name updated successfully", { duration: 2000 })
            setRename(false)
            updateRun(run.id, {
                ...searchableRun,
                name: name,
            })
        })
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleUpdateCollection()
        } else if (e.key === "Escape") {
            handleRenameReset()
        }
    }

    return (
        <div className="group relative flex w-full items-center">
            <Button variant="ghost" className={`p-1 text-left justify-start w-full text-muted-foreground ${isCurrent ? "bg-gray-200 text-accent-foreground" : ""}`} onClick={handleOnOpen}>
                {searchableRun.access === "private" ? <Lock /> : <Globe />}
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>{rename ? <input type="text" className="w-full bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none" defaultValue={name} onChange={(e) => setName(e.target.value)} onBlur={handleRenameReset} onKeyDown={handleOnKeyDown} autoFocus /> : <span className="truncate w-[100%] hover:w-[calc(100%-36px)]">{name}</span>}</TooltipTrigger>
                        <TooltipContent>
                            <p>{name}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </Button>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button size="icon" variant="ghost" className={`${isCurrent ? "hover:bg-gray-300 hover:text-accent-foreground" : ""}`}>
                            <MoreHorizontal />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="p-1 w-30">
                        <Button variant="ghost" onClick={handleOnOpen} className="text-left justify-start w-full text-muted-foreground">
                            <MessageSquareShare /> Open
                        </Button>
                        <Button variant="ghost" onClick={handleOnShare} className="text-left justify-start w-full text-muted-foreground">
                            <Share2 /> Share
                        </Button>
                        {collectionId && (
                            <Button variant="ghost" onClick={() => setRename(true)} className="text-left justify-start w-full text-muted-foreground">
                                <Pen /> Rename
                            </Button>
                        )}
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    )
}

function checkIfCurrent(runId: string | undefined, workflowId: string | undefined, searchableRun: SearchableExecutionRunRef) {
    return location.hash === `#${runId + "__" + workflowId}` || location.hash === `#${searchableRun.run.tags?.find((tag) => tag.startsWith("chat:"))?.replace("chat:", "")}` || location.hash === `#${searchableRun.run.tags?.find((tag) => tag.startsWith("service:"))?.replace("service:", "")}`
}

function extractHashId(searchableRun: SearchableExecutionRunRef) {
    if (searchableRun.run_id && searchableRun.workflow_id) {
        return `#${searchableRun.run_id + "__" + searchableRun.workflow_id}`
    }
    const chatTag = searchableRun.run.tags?.find((tag) => tag.startsWith("chat:"))
    if (chatTag) {
        return `#${chatTag.replace("chat:", "")}`
    }
    return `#${searchableRun.run.tags?.find((tag) => tag.startsWith("service:"))?.replace("service:", "")}`
}
